import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type Schedule = {
  id: string;
  site_id: string;
  site_name: string | null;
  preset_id: string | null;
  preset_name: string | null;
  name: string;
  cron_expression: string;
  posts_per_run: number;
  auto_dispatch: boolean;
  enabled: boolean;
  last_run_at: number | null;
  next_run_at: number | null;
  created_at: number;
};

export function useSchedules() {
  return useQuery({
    queryKey: ['schedules'],
    queryFn: () => api.get<{ schedules: Schedule[] }>('/api/app/schedules'),
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      site_id: string;
      preset_id?: string | null;
      name: string;
      cron_expression: string;
      posts_per_run?: number;
      auto_dispatch?: boolean;
      enabled?: boolean;
    }) => api.post<{ id: string }>('/api/app/schedules', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: Partial<Schedule & { cron_expression: string }> }) =>
      api.patch<{ updated: boolean }>(`/api/app/schedules/${args.id}`, args.body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<{ deleted: boolean }>(`/api/app/schedules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules'] }),
  });
}
